import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import useUploadersStore from './uploadStore'
import { PASSWORD_STATUSES, POLLING_CONFIG, UPLOAD_STAGES } from './constants'
import { parseBackendError } from './errorUtils'
import { uploadApi } from './api'
import { useWatchableRef } from '../../../lib/hooks'

export function useUploader(uploaderUUID) {
  const initUploaderAction = useUploadersStore((state) => state.initUploader)
  const initSubscriptionsAction = useUploadersStore((state) => state.initSubscriptions)
  const addFilesAction = useUploadersStore((state) => state.addFiles)
  const removeFileAction = useUploadersStore((state) => state.removeFile)
  const processQueueAction = useUploadersStore((state) => state.processQueue)
  const updateStatusAction = useUploadersStore((state) => state.updateStatus)
  const updatePasswordStatusAction = useUploadersStore((state) => state.updatePasswordStatus)
  const setErrorAction = useUploadersStore((state) => state.setError)
  const submitPasswordAction = useUploadersStore((state) => state.submitPassword)
  const removeFromReProcessItemsAction = useUploadersStore((state) => state.removeFromReProcessItems)
  const addUploadedFilesAction = useUploadersStore((state) => state.addUploadedFiles)
  const removeAllFilesAction = useUploadersStore((state) => state.removeAllFiles)

  const uploader = useUploadersStore((state) => state.uploaders[uploaderUUID])
  const queue = useUploadersStore((state) => state.uploaders[uploaderUUID]?.queue)
  const activeUploads = useUploadersStore((state) => state.uploaders[uploaderUUID]?.activeUploads)
  const reProcessItems = useUploadersStore((state) => state.uploaders[uploaderUUID]?.reProcessItems)
  const files = useUploadersStore((state) => state.uploaders[uploaderUUID]?.files)
  const errors = useUploadersStore((state) => state.uploaders[uploaderUUID]?.errors)
  const component = useUploadersStore((state) => state.uploaders[uploaderUUID]?.component)

  const initUploader = useCallback(
    (accepts, fileSizes, multiple, existingFiles, conversionFlags) =>
      initUploaderAction(uploaderUUID, {
        accepts,
        fileSizes,
        multiple,
        existingFiles,
        conversionFlags,
      }),
    [uploaderUUID, initUploaderAction]
  )
  const initSubscriptions = useCallback(
    ({ queueProcessingBatchSize }) => initSubscriptionsAction(uploaderUUID, { queueProcessingBatchSize }),
    [uploaderUUID, initSubscriptionsAction]
  )
  const addFiles = useCallback((files) => addFilesAction(uploaderUUID, files), [uploaderUUID, addFilesAction])
  const removeFile = useCallback(
    (fileUUID) => removeFileAction(uploaderUUID, fileUUID),
    [uploaderUUID, removeFileAction]
  )
  const processQueue = useCallback(
    (batchSize) => processQueueAction(uploaderUUID, batchSize),
    [uploaderUUID, processQueueAction]
  )
  const updateStatus = useCallback(
    (fileUUID, stage, metadata) => updateStatusAction(uploaderUUID, fileUUID, stage, metadata),
    [uploaderUUID, updateStatusAction]
  )
  const updatePasswordStatus = useCallback(
    (fileUUID, passwordStatus, isEncrypted = true) =>
      updatePasswordStatusAction(uploaderUUID, fileUUID, passwordStatus, isEncrypted),
    [uploaderUUID, updatePasswordStatusAction]
  )
  const submitPassword = useCallback(
    (fileUUID, password) => submitPasswordAction(uploaderUUID, fileUUID, password),
    [uploaderUUID, submitPasswordAction]
  )
  const setError = useCallback(
    (fileUUID, error) => setErrorAction(uploaderUUID, fileUUID, error),
    [uploaderUUID, setErrorAction]
  )
  const removeFromReProcessItems = useCallback(
    (fileUUID) => removeFromReProcessItemsAction(uploaderUUID, fileUUID),
    [uploaderUUID, removeFromReProcessItemsAction]
  )
  const addUploadedFiles = useCallback(
    (uploadedFiles) => addUploadedFilesAction(uploaderUUID, uploadedFiles),
    [uploaderUUID, addUploadedFilesAction]
  )
  const removeAllFiles = useCallback(
    (emit = true) => removeAllFilesAction(uploaderUUID, emit),
    [uploaderUUID, removeAllFilesAction]
  )

  return useMemo(
    () => ({
      uploader,
      initUploader,
      initSubscriptions,
      addFiles,
      removeFile,
      processQueue,
      updateStatus,
      updatePasswordStatus,
      setError,
      submitPassword,
      removeFromReProcessItems,
      addUploadedFiles,
      removeAllFiles,
      queue,
      activeUploads,
      reProcessItems,
      files,
      errors,
      component,
    }),
    [
      uploader,
      initUploader,
      initSubscriptions,
      addFiles,
      removeFile,
      processQueue,
      updateStatus,
      updatePasswordStatus,
      submitPassword,
      removeFromReProcessItems,
      addUploadedFiles,
      removeAllFiles,
      setError,
      queue,
      activeUploads,
      reProcessItems,
      files,
      errors,
      component,
    ]
  )
}

// TODO: Handle Cleanup
export function useInitUploader(
  uploaderUUID,
  { batchSize = 3, accepts = undefined, fileSizes, multiple, existingFiles, conversionFlags = {} }
) {
  const { uploader, initUploader, initSubscriptions } = useUploader(uploaderUUID)

  useEffect(() => {
    if (!uploader) {
      initUploader(accepts, fileSizes, multiple, existingFiles, conversionFlags)
      initSubscriptions({ queueProcessingBatchSize: batchSize })
    }
  }, [!!uploader, initUploader, accepts, fileSizes, multiple])
}

export function useGetColumns(breakpoint, onlyOne = true) {
  const [column, setColumn] = useState(1)
  const ref = useWatchableRef(null)

  useLayoutEffect(() => {
    setTimeout(() => {
      setColumn(onlyOne ? 1 : ref.current?.offsetWidth > breakpoint ? 2 : 1)
    }, 1)
  }, [ref.version, onlyOne])

  return useMemo(() => [column, ref.ref], [column, ref.version])
}
